// Live active-project indicator — shows the current conversation's project
// (General by default) and lets the operator switch it. Reads/writes through
// `useActiveProject` so agent-driven `project_set_active` calls surface here
// AND in the Projects sidebar from the same query cache — no drift.
import { Check, FolderGit2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { generalFirst, projectDisplayName } from '@onestone/shared/general';
import { Button } from '@onestone/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@onestone/ui/dropdown-menu';
import { api } from '@/lib/api';
import { STALE_SSE_COVERED_MS } from '@/lib/uiConstants';
import { useActiveProject } from '@/lib/useActiveProject';
import { cn } from '@onestone/ui/utils';

interface ActiveProjectIndicatorProps {
  className?: string;
}

export function ActiveProjectIndicator({ className }: ActiveProjectIndicatorProps) {
  const { activeProject, enabled, setActive, isSetting } = useActiveProject();

  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: () => api<{ projects: string[] }>(`/api/projects`),
    staleTime: STALE_SSE_COVERED_MS,
    retry: false,
  });

  // No conversation yet → nothing to anchor a project to.
  if (!enabled) return null;

  const list = generalFirst(projects.data?.projects ?? []);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn('h-7 max-w-[10rem] gap-1.5 px-2 text-xs font-medium text-foreground', className)}
          aria-label={`Active project: ${projectDisplayName(activeProject)}. Change`}
          title={`Active project: ${projectDisplayName(activeProject)}`}
        >
          <FolderGit2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">{projectDisplayName(activeProject)}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
        <DropdownMenuLabel>Active project</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {list.map((slug) => (
          <DropdownMenuItem key={slug} onSelect={() => void setActive(slug)} disabled={isSetting}>
            <FolderGit2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            <span className="truncate">{projectDisplayName(slug)}</span>
            {slug === activeProject ? <Check className="ml-auto h-3.5 w-3.5" aria-hidden="true" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
